import React, { useState, useEffect } from 'react';
import {
  Phone,
  Plus,
  Edit,
  Trash2,
  Volume2,
  MessageSquare,
  Building2,
  Settings,
  HelpCircle,
  Package,
  Briefcase,
  Check,
  X,
  RefreshCw
} from 'lucide-react';
import { demoAreaAPI, phoneAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';

const PIPELINE_OPTIONS = [
  { value: 'layer1', label: 'Layer 1 (OpenAI Realtime)', description: 'Low latency, OpenAI voices' },
  { value: 'layer2', label: 'Layer 2 (Claude + Cartesia)', description: 'Claude AI with Cartesia TTS' },
  { value: 'layer3', label: 'Layer 3 (Claude + ElevenLabs)', description: 'Claude AI with ElevenLabs voices' },
  { value: 'layer4', label: 'Layer 4 (Realtime + ElevenLabs)', description: 'Best transcription + ethnic voices' },
  { value: 'retell', label: 'Retell AI', description: 'Enterprise-grade voice AI with outbound calling' }
];

// Voice options organized by pipeline
const VOICE_OPTIONS_BY_PIPELINE = {
  layer1: [
    { value: 'alloy', label: 'Alloy' },
    { value: 'echo', label: 'Echo' },
    { value: 'fable', label: 'Fable' },
    { value: 'onyx', label: 'Onyx' },
    { value: 'nova', label: 'Nova' },
    { value: 'shimmer', label: 'Shimmer' }
  ],
  layer2: [
    { value: 'a0e99841-438c-4a64-b679-ae501e7d6091', label: 'Barbershop Man' },
    { value: '79a125e8-cd45-4c13-8a67-188112f4dd22', label: 'British Lady' },
    { value: '95856005-0332-41b0-935f-352e296aa0df', label: 'Classy British Man' },
    { value: 'ee7ea9f8-c0c1-498c-9f62-dc2627e1c6ec', label: 'Commercial Lady' },
    { value: 'c2ac25f9-ecc4-4f56-9095-651354df60c0', label: 'Commercial Man' },
    { value: '41534e16-2966-4c6b-9670-111411def906', label: 'Confident Woman' },
    { value: '5345cf08-6f37-424d-a5d9-8ae1571e1d1a', label: 'Helpful Woman' },
    { value: '638efaaa-4d0c-442e-b701-3fae16aad012', label: 'Indian Lady' }
  ],
  layer3: [
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  layer4: [
    { value: '9wYX8b0wRvLUEYtGuzP5', label: 'KeKe (Black Woman)' },
    { value: 'MEiBY6lrwud0dREUa5FQ', label: 'Brian (African American Radio Jockey)' },
    { value: 'YjlcD3XHztjJEo2wNszv', label: 'Tyre G (African American Male)' },
    { value: 'CwhRBWXzGAHq8TQ4Fs17', label: 'Roger (Laid-Back Male)' },
    { value: 'EXAVITQu4vr4xnSDxMaL', label: 'Sarah (Mature Female)' },
    { value: 'cgSgspJ2msm6clMCkdW9', label: 'Jessica (Playful Female)' },
    { value: 'Ogc84JIyxBeL4VxfwZTd', label: 'Elijah May' }
  ],
  retell: [
    { value: '11labs-Adrian', label: 'Adrian (Male)' },
    { value: '11labs-Rachel', label: 'Rachel (Female)' },
    { value: '11labs-Drew', label: 'Drew (Male)' },
    { value: '11labs-Clyde', label: 'Clyde (Male)' },
    { value: '11labs-Paul', label: 'Paul (Male)' },
    { value: '11labs-Domi', label: 'Domi (Female)' },
    { value: '11labs-Dave', label: 'Dave (Male)' },
    { value: '11labs-Fin', label: 'Fin (Male)' },
    { value: '11labs-Sarah', label: 'Sarah (Female)' },
    { value: '11labs-Antoni', label: 'Antoni (Male)' },
    { value: '11labs-Thomas', label: 'Thomas (Male)' },
    { value: '11labs-Charlie', label: 'Charlie (Male)' },
    { value: '11labs-Emily', label: 'Emily (Female)' },
    { value: '11labs-Elli', label: 'Elli (Female)' },
    { value: '11labs-Callum', label: 'Callum (Male)' }
  ]
};

// Default voice for each pipeline
const DEFAULT_VOICE_BY_PIPELINE = {
  layer1: 'alloy',
  layer2: 'a0e99841-438c-4a64-b679-ae501e7d6091',
  layer3: 'YjlcD3XHztjJEo2wNszv',
  layer4: '9wYX8b0wRvLUEYtGuzP5',  // KeKe (Black Woman)
  retell: '11labs-Adrian'
};

const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' }
];

const DemoAreaPage = () => {
  const [demoAreas, setDemoAreas] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    pipeline: 'layer1',
    voiceModel: 'alloy',
    language: 'en-US',
    businessName: '',
    businessDescription: '',
    greeting: '',
    instructions: '',
    // AI Configuration
    aiPersonality: '',
    aiTone: 'friendly',
    aiCapabilities: [],
    aiContext: '',
    faqs: [],
    products: [],
    services: []
  });

  // AI Capability options
  const AI_CAPABILITY_OPTIONS = [
    { value: 'take_orders', label: 'Take Orders', description: 'AI can take and confirm orders from callers' },
    { value: 'schedule_appointments', label: 'Schedule Appointments', description: 'AI can book appointments and meetings' },
    { value: 'answer_faqs', label: 'Answer FAQs', description: 'AI responds to frequently asked questions' },
    { value: 'transfer_calls', label: 'Transfer Calls', description: 'AI can transfer to specific departments or people' },
    { value: 'collect_info', label: 'Collect Information', description: 'AI gathers caller details and requirements' },
    { value: 'provide_quotes', label: 'Provide Quotes', description: 'AI can give pricing information or estimates' },
    { value: 'take_messages', label: 'Take Messages', description: 'AI takes messages for follow-up' },
    { value: 'process_payments', label: 'Process Payments', description: 'AI can handle payment information' }
  ];

  useEffect(() => {
    loadDemoAreas();
    loadPhoneNumbers();
  }, []);

  const loadDemoAreas = async () => {
    try {
      setLoading(true);
      const response = await demoAreaAPI.getDemoAreas();
      setDemoAreas(response.data.data || []);
    } catch (error) {
      console.error('Failed to load demo areas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPhoneNumbers = async () => {
    try {
      const response = await phoneAPI.getMyNumbers();
      setPhoneNumbers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      phoneNumber: '',
      pipeline: 'layer1',
      voiceModel: 'alloy',
      language: 'en-US',
      businessName: '',
      businessDescription: '',
      greeting: '',
      instructions: '',
      // AI Configuration
      aiPersonality: '',
      aiTone: 'friendly',
      aiCapabilities: [],
      aiContext: '',
      faqs: [],
      products: [],
      services: []
    });
    setEditingArea(null);
  };

  const openEditModal = (area) => {
    setEditingArea(area);
    setForm({
      name: area.name || '',
      phoneNumber: area.phoneNumber || '',
      pipeline: area.pipeline || 'layer1',
      voiceModel: area.voiceModel || 'alloy',
      language: area.language || 'en-US',
      businessName: area.businessName || '',
      businessDescription: area.businessDescription || '',
      greeting: area.greeting || '',
      instructions: area.instructions || '',
      // AI Configuration
      aiPersonality: area.aiPersonality || '',
      aiTone: area.aiTone || 'friendly',
      aiCapabilities: area.aiCapabilities || [],
      aiContext: area.aiContext || '',
      faqs: area.faqs || [],
      products: area.products || [],
      services: area.services || []
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // 🔍 DIAGNOSTIC: Log what we're about to save
      console.log('🔍 DEMO AREA SAVE - Form data:', {
        pipeline: form.pipeline,
        voiceModel: form.voiceModel,
        name: form.name,
        isEdit: !!editingArea,
        editingAreaId: editingArea?.id
      });

      if (editingArea) {
        const response = await demoAreaAPI.updateDemoArea(editingArea.id, form);
        // 🔍 DIAGNOSTIC: Log what was returned from API
        console.log('🔍 DEMO AREA SAVE - API response:', {
          savedPipeline: response?.data?.data?.pipeline,
          savedVoiceModel: response?.data?.data?.voiceModel
        });
      } else {
        await demoAreaAPI.createDemoArea(form);
      }
      setShowModal(false);
      resetForm();
      loadDemoAreas();
    } catch (error) {
      console.error('Failed to save demo area:', error);
      alert(error.response?.data?.error || 'Failed to save demo area');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await demoAreaAPI.deleteDemoArea(id);
      setDeleteConfirm(null);
      loadDemoAreas();
    } catch (error) {
      console.error('Failed to delete demo area:', error);
      alert('Failed to delete demo area');
    } finally {
      setLoading(false);
    }
  };

  // FAQ handlers
  const addFaq = () => {
    setForm(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const updateFaq = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) =>
        i === index ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const removeFaq = (index) => {
    setForm(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  // Product handlers
  const addProduct = () => {
    setForm(prev => ({
      ...prev,
      products: [...prev.products, { name: '', description: '', price: '' }]
    }));
  };

  const updateProduct = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.map((prod, i) =>
        i === index ? { ...prod, [field]: value } : prod
      )
    }));
  };

  const removeProduct = (index) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  // Service handlers
  const addService = () => {
    setForm(prev => ({
      ...prev,
      services: [...prev.services, { name: '', description: '', duration: '' }]
    }));
  };

  const updateService = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.map((svc, i) =>
        i === index ? { ...svc, [field]: value } : svc
      )
    }));
  };

  const removeService = (index) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const getPipelineLabel = (value) => {
    const option = PIPELINE_OPTIONS.find(o => o.value === value);
    return option ? option.label : value;
  };

  const getVoiceLabel = (voiceId, pipeline) => {
    const voiceOptions = VOICE_OPTIONS_BY_PIPELINE[pipeline] || [];
    const option = voiceOptions.find(o => o.value === voiceId);
    return option ? option.label : voiceId;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Demo Area Configuration</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure demo phone numbers with custom pipelines, voices, and business information
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadDemoAreas}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            New Demo Area
          </button>
        </div>
      </div>

      {/* Demo Area Cards */}
      {loading && demoAreas.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : demoAreas.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Demo Areas</h3>
          <p className="text-gray-500 mb-4">
            Create a demo area to configure a phone number with custom AI settings
          </p>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Create Demo Area
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {demoAreas.map((area) => (
            <div
              key={area.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{area.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" />
                    {area.phoneNumber ? formatPhoneNumber(area.phoneNumber) : 'No phone assigned'}
                  </p>
                </div>
                <span className={clsx(
                  'px-2 py-1 text-xs rounded-full',
                  area.isActive
                    ? 'bg-green-100 text-green-800'
                    : !area.phoneNumber
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-600'
                )}>
                  {area.isActive ? 'Active' : !area.phoneNumber ? 'No Phone' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span>{getPipelineLabel(area.pipeline)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  <span>Voice: {getVoiceLabel(area.voiceModel, area.pipeline)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span>{area.businessName}</span>
                </div>
              </div>

              <div className="flex gap-2 text-xs text-gray-500 mb-4">
                {area.faqs?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" />
                    {area.faqs.length} FAQs
                  </span>
                )}
                {area.products?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {area.products.length} Products
                  </span>
                )}
                {area.services?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {area.services.length} Services
                  </span>
                )}
              </div>

              <div className="flex gap-2 border-t border-gray-100 pt-3">
                <button
                  onClick={() => openEditModal(area)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => setDeleteConfirm(area.id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">
                  {editingArea ? 'Edit Demo Area' : 'Create Demo Area'}
                </h2>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Info */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Demo Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Sales Demo Line"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <select
                      value={form.phoneNumber || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, phoneNumber: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">No phone number (Inactive)</option>
                      {phoneNumbers.map((num) => (
                        <option key={num.id} value={num.phoneNumber}>
                          {formatPhoneNumber(num.phoneNumber)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Demo areas without a phone number are inactive but preserve configuration</p>
                  </div>
                </div>

                {/* Pipeline & Voice */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Pipeline & Voice</h3>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pipeline
                      </label>
                      <select
                        value={form.pipeline}
                        onChange={(e) => {
                          const newPipeline = e.target.value;
                          setForm(prev => ({
                            ...prev,
                            pipeline: newPipeline,
                            voiceModel: DEFAULT_VOICE_BY_PIPELINE[newPipeline]
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {PIPELINE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Voice Model
                      </label>
                      <select
                        value={form.voiceModel}
                        onChange={(e) => setForm(prev => ({ ...prev, voiceModel: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {VOICE_OPTIONS_BY_PIPELINE[form.pipeline].map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Language
                      </label>
                      <select
                        value={form.language}
                        onChange={(e) => setForm(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {LANGUAGE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Business Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={form.businessName}
                        onChange={(e) => setForm(prev => ({ ...prev, businessName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Acme Corporation"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Description
                      </label>
                      <textarea
                        rows={2}
                        value={form.businessDescription}
                        onChange={(e) => setForm(prev => ({ ...prev, businessDescription: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Brief description of the business..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Greeting
                      </label>
                      <textarea
                        rows={2}
                        value={form.greeting}
                        onChange={(e) => setForm(prev => ({ ...prev, greeting: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Hello! Thank you for calling..."
                      />
                    </div>
                  </div>
                </div>

                {/* FAQs */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-900">FAQs</h3>
                    <button
                      type="button"
                      onClick={addFaq}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add FAQ
                    </button>
                  </div>
                  {form.faqs.length === 0 ? (
                    <p className="text-sm text-gray-500">No FAQs added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {form.faqs.map((faq, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs text-gray-500">FAQ {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeFaq(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <input
                            type="text"
                            value={faq.question}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded mb-2"
                            placeholder="Question"
                          />
                          <textarea
                            rows={2}
                            value={faq.answer}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded"
                            placeholder="Answer"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Products */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Products</h3>
                    <button
                      type="button"
                      onClick={addProduct}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Product
                    </button>
                  </div>
                  {form.products.length === 0 ? (
                    <p className="text-sm text-gray-500">No products added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {form.products.map((prod, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs text-gray-500">Product {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeProduct(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid gap-2 md:grid-cols-3">
                            <input
                              type="text"
                              value={prod.name}
                              onChange={(e) => updateProduct(index, 'name', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Name"
                            />
                            <input
                              type="text"
                              value={prod.price}
                              onChange={(e) => updateProduct(index, 'price', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Price"
                            />
                            <input
                              type="text"
                              value={prod.description}
                              onChange={(e) => updateProduct(index, 'description', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Description"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Services */}
                <div className="border-t border-gray-100 pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Services</h3>
                    <button
                      type="button"
                      onClick={addService}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Service
                    </button>
                  </div>
                  {form.services.length === 0 ? (
                    <p className="text-sm text-gray-500">No services added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {form.services.map((svc, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs text-gray-500">Service {index + 1}</span>
                            <button
                              type="button"
                              onClick={() => removeService(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid gap-2 md:grid-cols-3">
                            <input
                              type="text"
                              value={svc.name}
                              onChange={(e) => updateService(index, 'name', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Name"
                            />
                            <input
                              type="text"
                              value={svc.duration}
                              onChange={(e) => updateService(index, 'duration', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Duration"
                            />
                            <input
                              type="text"
                              value={svc.description}
                              onChange={(e) => updateService(index, 'description', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded"
                              placeholder="Description"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* AI Configuration */}
                <div className="border-t border-gray-100 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">AI Configuration</h3>
                  <div className="space-y-4">
                    {/* AI Personality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Personality / Role
                      </label>
                      <textarea
                        rows={2}
                        value={form.aiPersonality}
                        onChange={(e) => setForm(prev => ({ ...prev, aiPersonality: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="e.g., You are a friendly restaurant host named Alex who helps customers with reservations and orders..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Define who the AI should be during the call</p>
                    </div>

                    {/* AI Tone */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tone
                      </label>
                      <select
                        value={form.aiTone}
                        onChange={(e) => setForm(prev => ({ ...prev, aiTone: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="friendly">Friendly - Warm and approachable</option>
                        <option value="professional">Professional - Polished and business-like</option>
                        <option value="casual">Casual - Relaxed and conversational</option>
                        <option value="enthusiastic">Enthusiastic - Energetic and upbeat</option>
                        <option value="warm">Warm - Genuinely caring and personable</option>
                        <option value="formal">Formal - Traditional and respectful</option>
                      </select>
                    </div>

                    {/* AI Capabilities */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        AI Capabilities
                      </label>
                      <p className="text-xs text-gray-500 mb-2">Select what the AI can do during calls</p>
                      <div className="grid grid-cols-2 gap-2">
                        {AI_CAPABILITY_OPTIONS.map((cap) => (
                          <label
                            key={cap.value}
                            className={`flex items-start gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                              form.aiCapabilities?.includes(cap.value)
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={form.aiCapabilities?.includes(cap.value) || false}
                              onChange={(e) => {
                                const current = form.aiCapabilities || [];
                                if (e.target.checked) {
                                  setForm(prev => ({ ...prev, aiCapabilities: [...current, cap.value] }));
                                } else {
                                  setForm(prev => ({ ...prev, aiCapabilities: current.filter(c => c !== cap.value) }));
                                }
                              }}
                              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900">{cap.label}</span>
                              <p className="text-xs text-gray-500">{cap.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* AI Context */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Internal Context (Not Spoken Aloud)
                      </label>
                      <textarea
                        rows={3}
                        value={form.aiContext}
                        onChange={(e) => setForm(prev => ({ ...prev, aiContext: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="e.g., Menu prices: Burger $12, Pizza $15. Hours: Mon-Fri 9am-9pm. Manager on duty: Sarah..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Background information the AI should know but NOT speak aloud (pricing, policies, etc.)</p>
                    </div>

                    {/* Additional Instructions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Instructions
                      </label>
                      <textarea
                        rows={3}
                        value={form.instructions}
                        onChange={(e) => setForm(prev => ({ ...prev, instructions: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Any additional instructions for the AI assistant..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Specific instructions for how the AI should handle calls</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (editingArea ? 'Update Demo Area' : 'Create Demo Area')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Demo Area</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this demo area? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoAreaPage;
